import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Scanner } from "@yudiel/react-qr-scanner";
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  WifiOff,
  Wifi,
  Loader2,
  Users,
  Ticket as TicketIcon,
  Search,
  RefreshCw,
  QrCode,
  ListChecks,
  CloudUpload,
  ClipboardList,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { scanApi, formatAuDate } from "../lib/events";
import { extractError } from "../lib/api";
import { Logo } from "../components/Logo";

// LocalStorage keys
const LS_DEVICE = "gt.scanner.device_id";
const LS_QUEUE = (token) => `gt.scanner.queue.${token}`;
const LS_HISTORY = (token) => `gt.scanner.history.${token}`;
const LS_REVOCATIONS = (token) => `gt.scanner.revocations.${token}`;

function getDeviceId() {
  let d = localStorage.getItem(LS_DEVICE);
  if (!d) {
    d = `dev-${Math.random().toString(36).slice(2, 8)}-${Date.now().toString(36)}`;
    localStorage.setItem(LS_DEVICE, d);
  }
  return d;
}

function loadJson(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key) || "null") ?? fallback; } catch { return fallback; }
}
function saveJson(key, value) { localStorage.setItem(key, JSON.stringify(value)); }

function useOnline() {
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  return online;
}

const RESULT_META = {
  ok: { label: "Admit", tone: "ok", icon: CheckCircle2, bg: "bg-emerald-500", ring: "ring-emerald-500" },
  already_scanned: { label: "Already scanned", tone: "warn", icon: AlertTriangle, bg: "bg-amber-500", ring: "ring-amber-500" },
  queued: { label: "Pending verification", tone: "warn", icon: AlertTriangle, bg: "bg-amber-500", ring: "ring-amber-500" },
  invalid_unknown: { label: "Unknown ticket", tone: "bad", icon: XCircle, bg: "bg-[var(--gt-accent)]", ring: "ring-[var(--gt-accent)]" },
  invalid_void: { label: "Void / refunded", tone: "bad", icon: XCircle, bg: "bg-[var(--gt-accent)]", ring: "ring-[var(--gt-accent)]" },
  invalid_revoked: { label: "Revoked — do not admit", tone: "bad", icon: XCircle, bg: "bg-[var(--gt-accent)]", ring: "ring-[var(--gt-accent)]" },
  invalid_wrong_event: { label: "Wrong event", tone: "bad", icon: XCircle, bg: "bg-[var(--gt-accent)]", ring: "ring-[var(--gt-accent)]" },
};

function ResultCard({ result, ticket, first_used_at, conflict, conflict_reason, offline }) {
  const meta = RESULT_META[result] || RESULT_META.invalid_unknown;
  const Icon = meta.icon;
  return (
    <div
      className={`border-2 border-[var(--gt-ink)] bg-white shadow-[6px_6px_0px_0px_var(--gt-ink)] p-6 ring-4 ${meta.ring}`}
      data-testid={`scan-result-${result}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className={`${meta.bg} text-white p-2 rounded-sm`}>
          <Icon size={22} strokeWidth={2.5} />
        </div>
        <div>
          <div className="gt-label">Scan result</div>
          <h3 className="font-heading text-2xl font-black tracking-tighter leading-none" data-testid="scan-result-label">
            {meta.label}
          </h3>
        </div>
      </div>

      {ticket && (
        <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <div><div className="gt-label mb-0.5">Type</div><div className="font-semibold" data-testid="scan-ticket-type">{ticket.ticket_type_name || "—"}</div></div>
          <div><div className="gt-label mb-0.5">Attendee</div><div className="font-semibold">{ticket.buyer_name || "—"}</div></div>
          <div className="col-span-2"><div className="gt-label mb-0.5">Code</div><div className="font-mono font-bold" data-testid="scan-ticket-code">{ticket.code}</div></div>
          {ticket.token_number && (
            <div
              className="col-span-2 mt-2 border-2 border-[var(--gt-ink)] bg-yellow-100 p-3"
              data-testid="scan-ticket-token-panel"
            >
              <div className="gt-label mb-1" data-testid="scan-ticket-token-label">
                {ticket.token_label || "Token No."}
              </div>
              <div
                className="font-mono text-3xl font-black tracking-tight text-[var(--gt-ink)]"
                data-testid="scan-ticket-token-number"
              >
                {ticket.token_number}
              </div>
            </div>
          )}
        </div>
      )}

      {first_used_at && (
        <div className="mt-4 text-xs text-[var(--gt-ink-2)]">
          First checked in: <span className="font-mono">{formatAuDate(first_used_at)}</span>
        </div>
      )}

      {conflict && (
        <div className="mt-4 border-2 border-[var(--gt-accent)] bg-red-50 text-[var(--gt-ink)] text-xs p-3" data-testid="scan-conflict-banner">
          <div className="font-bold uppercase tracking-widest mb-1">Sync conflict</div>
          <div className="text-[var(--gt-ink-2)]">{conflict_reason || "This ticket was scanned by another device before your offline scan synced."}</div>
        </div>
      )}

      {offline && result === "queued" && (
        <div className="mt-4 border-2 border-amber-500 bg-amber-50 text-[var(--gt-ink)] text-xs p-3" data-testid="scan-offline-pending">
          <div className="font-bold uppercase tracking-widest mb-1">Offline · pending verification</div>
          <div className="text-[var(--gt-ink-2)]">Queued locally. This ticket has NOT been verified against the server — do not admit on trust until it syncs.</div>
        </div>
      )}
      {offline && result === "invalid_revoked" && (
        <div className="mt-4 border-2 border-[var(--gt-accent)] bg-red-50 text-[var(--gt-ink)] text-xs p-3" data-testid="scan-offline-revoked">
          <div className="font-bold uppercase tracking-widest mb-1">Revoked — do not admit</div>
          <div className="text-[var(--gt-ink-2)]">This ticket was voided and is on the offline revocation list. Reject entry.</div>
        </div>
      )}
    </div>
  );
}

export default function ScannerPage() {
  const { token } = useParams();
  const online = useOnline();
  const deviceId = useMemo(getDeviceId, []);
  const api = useMemo(() => scanApi(token), [token]);

  const [session, setSession] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("scan"); // 'scan' | 'manual' | 'log'
  const [lastResult, setLastResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [queue, setQueue] = useState(() => loadJson(LS_QUEUE(token), []));
  const [history, setHistory] = useState(() => loadJson(LS_HISTORY(token), []));
  const [revocations, setRevocations] = useState(() => loadJson(LS_REVOCATIONS(token), { version: 0, codes: {}, synced_at: null }));
  const [attendees, setAttendees] = useState([]);
  const [query, setQuery] = useState("");
  const [manualCode, setManualCode] = useState("");
  const [cameraOn, setCameraOn] = useState(true);
  const throttleRef = useRef({ code: "", at: 0 });

  // Persist queue+history
  useEffect(() => { saveJson(LS_QUEUE(token), queue); }, [token, queue]);
  useEffect(() => { saveJson(LS_HISTORY(token), history.slice(0, 200)); }, [token, history]);
  useEffect(() => { saveJson(LS_REVOCATIONS(token), revocations); }, [token, revocations]);

  // Sprint — offline revocation delta-sync. Downloads revoked ticket codes so a
  // voided ticket can be actively rejected even while offline.
  const syncRevocations = useCallback(async () => {
    try {
      const since = revocations.version || 0;
      const r = await api.revocations(since);
      setRevocations((prev) => {
        const codes = r.full ? {} : { ...prev.codes };
        (r.revocations || []).forEach((x) => { if (x.code) codes[x.code] = true; });
        return { version: r.version, codes, synced_at: r.synced_at };
      });
      return r;
    } catch (e) {
      return null;
    }
  }, [api, revocations.version]);

  // Initial + periodic revocation sync while online
  useEffect(() => {
    if (online) syncRevocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);

  const refreshSession = useCallback(async () => {
    try {
      const s = await api.session();
      setSession(s);
      setError("");
    } catch (e) {
      setError(extractError(e));
    }
  }, [api]);

  useEffect(() => { refreshSession(); }, [refreshSession]);

  // Auto refresh stats every 15s when online
  useEffect(() => {
    if (!online) return;
    const t = setInterval(() => {
      api.stats().then((s) => setSession((prev) => prev ? { ...prev, stats: s } : prev)).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, [online, api]);

  const enqueueLocal = useCallback((entry) => {
    setQueue((q) => [...q, entry]);
    setHistory((h) => [entry, ...h]);
  }, []);

  const processResult = useCallback((code, apiResult, offline = false, clientScanId = null) => {
    const entry = {
      client_scan_id: clientScanId || `${deviceId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      ticket_code: code,
      device_id: deviceId,
      scanned_at: new Date().toISOString(),
      result: apiResult?.result,
      offline,
      conflict: apiResult?.conflict || false,
      conflict_reason: apiResult?.conflict_reason || null,
      ticket: apiResult?.ticket || null,
      first_used_at: apiResult?.first_used_at || null,
    };
    setHistory((h) => [entry, ...h]);
    setLastResult(entry);
    if (apiResult?.result === "ok") {
      setSession((prev) => prev ? { ...prev, stats: { ...prev.stats, checked_in: (prev.stats?.checked_in || 0) + 1 } } : prev);
    }
  }, [deviceId]);

  // Shared offline handler: actively reject a locally-known revoked ticket,
  // otherwise queue as "pending verification". Used by BOTH the camera scan
  // path and the manual keyed check-in path so offline behaviour never diverges.
  const handleOffline = useCallback((code) => {
    const clientScanId = `${deviceId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    if (revocations.codes && revocations.codes[code]) {
      const entry = {
        client_scan_id: clientScanId, ticket_code: code, device_id: deviceId,
        scanned_at: new Date().toISOString(), result: "invalid_revoked", offline: true,
      };
      setHistory((h) => [entry, ...h]);
      setLastResult({ result: "invalid_revoked", ticket: { code }, offline: true });
      toast.error("Revoked ticket — do not admit");
      return;
    }
    enqueueLocal({
      client_scan_id: clientScanId, ticket_code: code, device_id: deviceId,
      scanned_at: new Date().toISOString(), result: "queued", offline: true,
    });
    setLastResult({ result: "queued", ticket: { code }, offline: true });
    toast.warning("Offline · pending verification");
  }, [deviceId, revocations, enqueueLocal]);

  const handleScan = useCallback(async (code) => {
    if (!code || busy) return;
    const now = Date.now();
    // debounce: same code within 2s ignored
    if (throttleRef.current.code === code && now - throttleRef.current.at < 2000) return;
    throttleRef.current = { code, at: now };
    setBusy(true);
    const clientScanId = `${deviceId}-${now}-${Math.random().toString(36).slice(2, 6)}`;

    if (!online) {
      handleOffline(code);
      setBusy(false);
      return;
    }

    try {
      const r = await api.validate({
        ticket_code: code, device_id: deviceId, client_scan_id: clientScanId, was_offline: false,
      });
      processResult(code, r, false, clientScanId);
      if (r.result === "ok") toast.success("Admit");
      else if (r.result === "already_scanned") toast.warning("Already scanned");
      else toast.error(RESULT_META[r.result]?.label || "Invalid");
    } catch (e) {
      toast.error(extractError(e));
    } finally { setBusy(false); }
  }, [api, busy, deviceId, online, processResult, handleOffline]);

  const handleManualCheckin = async (e) => {
    e.preventDefault();
    const code = manualCode.trim();
    if (!code) return;
    if (!online) {
      handleOffline(code);
      setManualCode("");
      return;
    }
    setBusy(true);
    try {
      const r = await api.manual({ ticket_code: code, device_id: deviceId });
      processResult(code, r, false);
      setManualCode("");
      if (r.result === "ok") toast.success("Manual check-in complete");
      else toast.warning(RESULT_META[r.result]?.label || "Check that ticket");
      // Refresh door list + stats so the row flips to 'Checked in' immediately
      loadAttendees(query);
      refreshSession();
    } catch (err) { toast.error(extractError(err)); }
    finally { setBusy(false); }
  };

  const syncQueue = useCallback(async () => {
    if (!online || queue.length === 0) return;
    setBusy(true);
    try {
      const r = await api.sync({
        device_id: deviceId,
        scans: queue.map((q) => ({
          ticket_code: q.ticket_code,
          device_id: q.device_id,
          client_scan_id: q.client_scan_id,
          scanned_at: q.scanned_at,
          was_offline: true,
        })),
      });
      // Merge results back into history
      const map = new Map(r.results.map((x) => [x.client_scan_id, x]));
      setHistory((h) => h.map((e) => {
        const m = map.get(e.client_scan_id);
        if (!m) return e;
        if (e.result === "queued" || e.offline) {
          return {
            ...e, result: m.result, conflict: m.conflict, conflict_reason: m.conflict_reason,
            ticket: m.ticket || e.ticket, first_used_at: m.first_used_at || null, synced: true,
          };
        }
        return e;
      }));
      const conflicts = r.results.filter((x) => x.conflict).length;
      const oks = r.results.filter((x) => x.result === "ok").length;
      setQueue([]);
      toast.success(`Synced ${r.processed} scan(s)${conflicts ? ` · ${conflicts} conflict(s)` : ""}`);
      refreshSession();
      syncRevocations();
      return { oks, conflicts };
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusy(false); }
  }, [api, deviceId, online, queue, refreshSession, syncRevocations]);

  // Auto-sync when coming back online
  useEffect(() => {
    if (online && queue.length > 0) syncQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);

  const loadAttendees = useCallback(async (q = "") => {
    if (!online) return; // door list needs the server; stay silent while offline
    try { setAttendees(await api.attendees(q)); } catch (e) { if (online) toast.error(extractError(e)); }
  }, [api, online]);

  useEffect(() => {
    if (tab === "manual") loadAttendees(query);
  }, [tab, loadAttendees, query]);

  if (error && !session) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-[var(--gt-bg-marketing)]" data-testid="scanner-error">
        <div className="max-w-sm w-full text-center">
          <XCircle size={40} className="mx-auto text-[var(--gt-accent)] mb-3" />
          <div className="gt-label mb-2">Scanner locked</div>
          <h1 className="font-heading text-3xl font-black tracking-tighter mb-3">Can&apos;t open scanner</h1>
          <p className="text-[var(--gt-ink-2)] text-sm mb-6" data-testid="scanner-error-message">{error}</p>
          <Link to="/" className="gt-btn-ghost text-sm">Back to Getickets</Link>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="scanner-loading">
        <Loader2 className="animate-spin text-[var(--gt-ink-2)]" size={22} />
      </div>
    );
  }

  const pct = session.stats.total ? Math.round((session.stats.checked_in / session.stats.total) * 100) : 0;

  return (
    <div className="min-h-screen bg-[var(--gt-bg-marketing)]" data-testid="scanner-page">
      {/* Sticky header */}
      <header className="sticky top-0 z-40 bg-[var(--gt-ink)] text-white border-b-2 border-[var(--gt-ink)]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Logo inverse size="sm" />
              <span className="text-[10px] tracking-[0.25em] uppercase text-[var(--gt-ink-3)]">Door scanner</span>
            </div>
            <div className="mt-1 font-heading text-lg font-black tracking-tight truncate" data-testid="scanner-event-title">
              {session.event.title}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)]">
              {formatAuDate(session.event.start_at)} · {session.event.venue_name}, {session.event.state}
            </div>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <div
              className={`flex items-center gap-1 px-2 py-1 rounded-sm border ${online ? "border-emerald-400 text-emerald-300" : "border-amber-400 text-amber-300"}`}
              data-testid={online ? "scanner-online" : "scanner-offline"}
            >
              {online ? <Wifi size={12} /> : <WifiOff size={12} />}
              <span className="text-[10px] uppercase tracking-widest">{online ? "Online" : "Offline"}</span>
            </div>
            <div
              className={`hidden sm:flex items-center gap-1 px-2 py-1 rounded-sm border ${revocations.synced_at ? "border-emerald-400 text-emerald-300" : "border-amber-400 text-amber-300"}`}
              data-testid="scanner-revocation-status"
              title={revocations.synced_at ? `Revocations last synced at ${formatAuDate(revocations.synced_at)}` : "Not synced yet"}
            >
              <ShieldCheck size={12} />
              <span className="text-[10px] uppercase tracking-widest">
                {revocations.synced_at ? "Ready for offline" : "Sync required"}
              </span>
            </div>
            {queue.length > 0 && (
              <button
                onClick={syncQueue}
                className="flex items-center gap-1 px-2 py-1 border border-amber-400 text-amber-200 rounded-sm text-[10px] uppercase tracking-widest disabled:opacity-40"
                disabled={!online || busy}
                data-testid="scanner-sync-btn"
              >
                <CloudUpload size={12} /> Sync {queue.length}
              </button>
            )}
          </div>
        </div>
        {/* Stats bar */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-3">
          <div className="flex items-center justify-between text-xs text-[var(--gt-ink-3)] mb-1">
            <span data-testid="scanner-stats-label">
              Checked in <span className="font-mono text-white" data-testid="scanner-checked-in">{session.stats.checked_in}</span> / <span className="font-mono text-white" data-testid="scanner-total">{session.stats.total}</span>
            </span>
            <span className="font-mono">{pct}%</span>
          </div>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-400 transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        {/* Tabs */}
        <div className="flex items-center gap-2 mb-6 border-b-2 border-[var(--gt-ink)]">
          <TabButton active={tab === "scan"} onClick={() => setTab("scan")} icon={QrCode} label="Scan" testid="tab-scan" />
          <TabButton active={tab === "manual"} onClick={() => setTab("manual")} icon={ListChecks} label="Manual" testid="tab-manual" />
          <TabButton active={tab === "log"} onClick={() => setTab("log")} icon={ClipboardList} label="Log" testid="tab-log" />
        </div>

        {tab === "scan" && (
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-6">
            <div>
              <div className="gt-label mb-2">Camera</div>
              <div className="relative border-2 border-[var(--gt-ink)] bg-black aspect-square overflow-hidden shadow-[6px_6px_0px_0px_var(--gt-ink)]">
                {cameraOn ? (
                  <Scanner
                    onScan={(codes) => { if (codes?.length) handleScan(codes[0].rawValue); }}
                    onError={() => {}}
                    formats={["qr_code"]}
                    styles={{ container: { width: "100%", height: "100%" }, video: { objectFit: "cover" } }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/60 text-sm" data-testid="camera-paused">Camera paused</div>
                )}
                {busy && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <Loader2 className="animate-spin text-white" size={26} />
                  </div>
                )}
              </div>
              <div className="mt-3 flex items-center justify-between text-xs">
                <button
                  onClick={() => setCameraOn((v) => !v)}
                  className="gt-btn-ghost text-xs"
                  data-testid="toggle-camera-btn"
                >
                  {cameraOn ? "Pause camera" : "Resume camera"}
                </button>
                <span className="text-[var(--gt-ink-3)]" data-testid="scanner-device-id">
                  Device: <span className="font-mono">{deviceId}</span>
                </span>
              </div>
            </div>

            <div>
              <div className="gt-label mb-2">Last scan</div>
              {lastResult ? (
                <ResultCard
                  result={lastResult.result}
                  ticket={lastResult.ticket}
                  first_used_at={lastResult.first_used_at}
                  conflict={lastResult.conflict}
                  conflict_reason={lastResult.conflict_reason}
                  offline={lastResult.offline}
                />
              ) : (
                <div className="border-2 border-dashed border-[var(--gt-line)] p-6 text-center text-sm text-[var(--gt-ink-3)]" data-testid="scanner-idle">
                  Point the camera at a Getickets QR to admit an attendee.
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "manual" && (
          <div>
            {lastResult && (
              <div className="mb-6" data-testid="manual-last-result">
                <ResultCard
                  result={lastResult.result}
                  ticket={lastResult.ticket}
                  first_used_at={lastResult.first_used_at}
                  conflict={lastResult.conflict}
                  conflict_reason={lastResult.conflict_reason}
                  offline={lastResult.offline}
                />
              </div>
            )}
            <form onSubmit={handleManualCheckin} className="gt-card mb-6" data-testid="manual-checkin-form">
              <div className="gt-label mb-2">Check in by code</div>
              <div className="flex gap-2">
                <input
                  className="gt-input flex-1"
                  placeholder="TKT-XXXXXXXXXX"
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value.toUpperCase())}
                  data-testid="manual-code-input"
                />
                <button type="submit" className="gt-btn-primary" disabled={busy || !manualCode} data-testid="manual-checkin-btn">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Check in
                </button>
              </div>
            </form>

            <div className="gt-card">
              <div className="flex items-center justify-between mb-3">
                <div className="gt-label">Door list</div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--gt-ink-3)]" />
                    <input
                      className="gt-input pl-7 py-1.5 text-xs w-48"
                      placeholder="Search name/email/code"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") loadAttendees(query); }}
                      data-testid="attendee-search-input"
                    />
                  </div>
                  <button onClick={() => loadAttendees(query)} className="gt-btn-ghost text-xs" data-testid="attendee-refresh-btn"><RefreshCw size={12} /></button>
                </div>
              </div>
              <ul className="divide-y divide-[var(--gt-line)]" data-testid="attendees-list">
                {attendees.length === 0 && (
                  <li className="py-6 text-center text-xs text-[var(--gt-ink-3)]">No matching attendees.</li>
                )}
                {attendees.map((t) => (
                  <li key={t.id} className="py-3 flex items-center justify-between gap-3" data-testid={`attendee-row-${t.code}`}>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm truncate">{t.buyer_name || "(No name)"}</div>
                      <div className="text-xs text-[var(--gt-ink-2)] truncate">{t.buyer_email}</div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--gt-ink-3)] mt-0.5">
                        {t.ticket_type_name} · {t.code}
                      </div>
                    </div>
                    <div>
                      {t.status === "checked_in" ? (
                        <span className="text-[10px] uppercase tracking-widest text-emerald-600 font-bold">Checked in</span>
                      ) : (
                        <button
                          onClick={async () => {
                            try {
                              const r = await api.manual({ ticket_code: t.code, device_id: deviceId });
                              processResult(t.code, r);
                              loadAttendees(query);
                              refreshSession();
                            } catch (e) { toast.error(extractError(e)); }
                          }}
                          className="gt-btn-primary text-xs py-1.5"
                          data-testid={`attendee-checkin-${t.code}`}
                        >
                          Check in
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {tab === "log" && (
          <div className="gt-card" data-testid="scan-log">
            <div className="flex items-center justify-between mb-3">
              <div className="gt-label">Scan history (local device)</div>
              <span className="text-xs text-[var(--gt-ink-3)]">{history.length} entries</span>
            </div>
            {history.length === 0 && (
              <div className="py-6 text-center text-xs text-[var(--gt-ink-3)]">No scans yet.</div>
            )}
            <ul className="divide-y divide-[var(--gt-line)]">
              {history.slice(0, 100).map((h) => {
                const meta = RESULT_META[h.result] || { label: h.result, tone: "warn" };
                return (
                  <li key={h.client_scan_id} className="py-2 text-xs flex items-center justify-between gap-2" data-testid={`log-entry-${h.client_scan_id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-mono font-bold truncate">{h.ticket_code}</div>
                      <div className="text-[10px] text-[var(--gt-ink-3)]">{formatAuDate(h.scanned_at)}</div>
                    </div>
                    <div className="text-right">
                      <div className={`text-[10px] uppercase tracking-widest font-bold ${
                        h.result === "ok" ? "text-emerald-600"
                        : h.result === "already_scanned" ? "text-amber-600"
                        : h.result === "queued" ? "text-[var(--gt-ink-2)]"
                        : "text-[var(--gt-accent)]"
                      }`}>
                        {meta.label}
                        {h.offline && !h.synced && h.result === "queued" && <span className="ml-1 text-[var(--gt-ink-3)]">· pending</span>}
                        {h.conflict && <span className="ml-1 text-[var(--gt-accent)]" data-testid={`log-conflict-${h.client_scan_id}`}>· conflict</span>}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 text-sm font-semibold border-b-2 -mb-0.5 ${
        active ? "border-[var(--gt-accent)] text-[var(--gt-ink)]" : "border-transparent text-[var(--gt-ink-3)] hover:text-[var(--gt-ink)]"
      }`}
      data-testid={testid}
    >
      <Icon size={14} /> {label}
    </button>
  );
}
